import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import toast from 'react-hot-toast';
import WalletInfoCard from './WalletInfoCard';
import DiscountDetails from './DiscountDetails';

// CSS stílusok a leírás tartalmának megfelelő megjelenítéséhez
const descriptionStyles = `
  .discount-description {
    max-height: 200px;
    overflow-y: auto;
    margin-top: 0.5rem;
    margin-bottom: 1rem;
    line-height: 1.5;
  }
  .discount-description h1, .discount-description h2, .discount-description h3 {
    margin-top: 0.5em;
    margin-bottom: 0.5em;
    font-weight: 600;
  }
  .discount-description h1 {
    font-size: 1.2rem;
  }
  .discount-description h2 {
    font-size: 1.1rem;
  }
  .discount-description h3 {
    font-size: 1.05rem;
  }
  .discount-description p {
    margin-bottom: 0.75em;
  }
  .discount-description ul, .discount-description ol {
    margin-left: 1.5em;
    margin-bottom: 0.5em;
  }
  .discount-description li {
    margin-bottom: 0.25em;
  }
  .discount-description a {
    color: #4f46e5;
    text-decoration: underline;
  }
  .discount-description img {
    max-width: 100%;
    height: auto;
  }
`;

export default function Dashboard() {
  const { t } = useTranslation();
  const [discounts, setDiscounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [walletInfo, setWalletInfo] = useState(null);
  const [selectedDiscountId, setSelectedDiscountId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Kategóriák lekérése
        const categoriesSnapshot = await getDocs(collection(db, 'discountCategories'));
        const categoriesData = categoriesSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(cat => cat.isActive); // Csak az aktív kategóriákat jelenítjük meg
        setCategories(categoriesData);

        // Kedvezmények lekérése
        const discountsSnapshot = await getDocs(collection(db, 'discounts'));
        const discountsData = discountsSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(discount => discount.isActive);

        // Kategória információk hozzáadása a kedvezményekhez
        const discountsWithCategories = discountsData.map(discount => ({
          ...discount,
          category: categoriesData.find(cat => cat.id === discount.categoryId)
        }));

        setDiscounts(discountsWithCategories);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Szűrt kedvezmények
  const filteredDiscounts = selectedCategory === 'all' 
    ? discounts 
    : discounts.filter(d => d.categoryId === selectedCategory);

  // Az Icons dinamikus használatához adjunk hozzá egy segédfüggvényt:
  const DynamicIcon = ({ iconName, ...props }) => {
    const Icon = Icons[iconName];
    return Icon ? <Icon {...props} /> : null;
  };

  // Ha van kiválasztott kedvezmény, akkor a részletes oldalt jelenítjük meg
  if (selectedDiscountId) {
    return (
      <div className="bg-white w-full">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <DiscountDetails 
            discountId={selectedDiscountId} 
            onBack={() => setSelectedDiscountId(null)} 
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white w-full">
      <style>{descriptionStyles}</style>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        {/* Info Section */}
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg overflow-hidden">
          <div className="relative">
            {/* Háttér dekoráció */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute -right-20 -top-20 w-72 h-72 bg-white rounded-full"></div>
              <div className="absolute -left-20 -bottom-20 w-72 h-72 bg-white rounded-full"></div>
            </div>
            
            <div className="relative p-8 text-white">
              <h2 className="text-3xl font-bold mb-6">
                Üdvözöljük az Előny Klubban!
              </h2>
              
              <p className="text-lg text-indigo-100 mb-8 leading-relaxed max-w-3xl">
                Ez egy olyan klub, ahol mindenki talál olyan előnyt, ami megdobogtatja a szívét, 
                könnyebbséget jelent a hétköznapi költségek viselésében, mérsékli a kiadásokat, 
                elérhetővé teszi azt, ami eddig csak álom volt.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white bg-opacity-10 rounded-lg p-6 backdrop-blur-sm">
                  <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center mb-4">
                    <Icons.Star className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    Exkluzív kedvezmények
                  </h3>
                  <p className="text-indigo-100">
                    Csak klubtagoknak elérhető különleges ajánlatok
                  </p>
                </div>
                
                <div className="bg-white bg-opacity-10 rounded-lg p-6 backdrop-blur-sm">
                  <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center mb-4">
                    <Icons.Shield className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    Ellenőrzött partnerek
                  </h3>
                  <p className="text-indigo-100">
                    Megbízható ajánlatok válogatott szolgáltatóktól
                  </p>
                </div>
                
                <div className="bg-white bg-opacity-10 rounded-lg p-6 backdrop-blur-sm">
                  <div className="bg-white rounded-full w-12 h-12 flex items-center justify-center mb-4">
                    <Icons.RefreshCw className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    Folyamatos frissítés
                  </h3>
                  <p className="text-indigo-100">
                    Rendszeresen új kedvezmények és lehetőségek
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wallet Info Card */}
        <WalletInfoCard />

        {/* Kategória szűrő */}
        <div className="space-y-6">
          {/* Címsor és alcím */}
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-purple-600">
              Elérhető kedvezmények
            </h1>
            <p className="mt-3 text-gray-600">
              Válogass kedvedre az elérhető kedvezmények között és találd meg a számodra legjobbat
            </p>
          </div>

          {/* Kategória csempék */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`relative flex flex-col items-center justify-center p-6 rounded-xl transition-all transform hover:scale-105 ${
                  selectedCategory === 'all'
                    ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg'
                    : 'bg-gray-50 text-gray-600 hover:bg-indigo-50 hover:shadow-md'
                } border border-gray-100`}
              >
                <Icons.LayoutGrid className={`h-8 w-8 mb-3 ${
                  selectedCategory === 'all' ? 'text-white' : 'text-indigo-500'
                }`} />
                <span className="font-medium">Összes</span>
                <p className={`text-xs mt-1 text-center ${
                  selectedCategory === 'all' ? 'text-white/80' : 'text-gray-500'
                }`}>
                  Minden elérhető kedvezmény
                </p>
                <span className={`absolute top-3 right-3 ${
                  selectedCategory === 'all'
                    ? 'bg-white text-indigo-600'
                    : 'bg-indigo-100 text-indigo-600'
                } px-2 py-0.5 rounded-full text-xs font-bold`}>
                  {discounts.length}
                </span>
              </button>
              
              {categories.map(category => {
                const count = discounts.filter(d => d.categoryId === category.id).length;
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`relative flex flex-col items-center justify-center p-6 rounded-xl transition-all transform hover:scale-105 ${
                      selectedCategory === category.id
                        ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg'
                        : 'bg-gray-50 text-gray-600 hover:bg-indigo-50 hover:shadow-md'
                    } border border-gray-100`}
                  >
                    <DynamicIcon 
                      iconName={category.icon} 
                      className={`h-8 w-8 mb-3 ${
                        selectedCategory === category.id ? 'text-white' : 'text-indigo-500'
                      }`}
                    />
                    <span className="font-medium text-center">{category.name}</span>
                    <p className={`text-xs mt-1 text-center ${
                      selectedCategory === category.id ? 'text-white/80' : 'text-gray-500'
                    }`}>
                      {category.description}
                    </p>
                    {count > 0 && (
                      <span className={`absolute top-3 right-3 ${
                        selectedCategory === category.id
                          ? 'bg-white text-indigo-600'
                          : 'bg-indigo-100 text-indigo-600'
                      } px-2 py-0.5 rounded-full text-xs font-bold`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Kedvezmények megjelenítése */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Icons.Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
          </div>
        ) : filteredDiscounts.length === 0 ? (
          <div className="text-center py-12">
            <Icons.Package className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {selectedCategory === 'all' 
                ? t('dashboard.noDiscounts')
                : `Nincsenek kedvezmények a "${categories.find(c => c.id === selectedCategory)?.name}" kategóriában`}
            </h3>
            <p className="mt-1 text-sm text-gray-500">{t('dashboard.checkBackLater')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {filteredDiscounts.map((discount) => (
              <div 
                key={discount.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group relative h-full cursor-pointer"
                onClick={() => setSelectedDiscountId(discount.id)}
              >
                <div className="block">
                  {discount.logoUrl && (
                    <div className="aspect-w-16 aspect-h-9">
                      <img
                        src={discount.logoUrl}
                        alt={discount.title}
                        className="w-full h-56 object-cover"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900 group-hover:text-indigo-600 transition-colors">
                        {discount.title}
                      </h3>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {discount.percentage}%
                      </span>
                    </div>
                    
                    {/* Kategória és kuponkód sor */}
                    <div className="mt-2 flex items-center justify-between">
                      {/* Kategória */}
                      {discount.category && (
                        <div className="flex items-center">
                          <DynamicIcon 
                            iconName={discount.category.icon} 
                            className="h-4 w-4 mr-2 text-gray-500" 
                          />
                          <span className="text-xs font-medium text-gray-600">
                            {discount.category.name}
                          </span>
                        </div>
                      )}
                      
                      {/* Kuponkód */}
                      {discount.couponCode && (
                        <button
                          onClick={(e) => {
                            e.preventDefault(); // Megakadályozza a link aktiválását
                            e.stopPropagation(); // Megakadályozza a buborékolást (a kártya kattintás eseményét)
                            setSelectedCoupon({
                              code: discount.couponCode,
                              title: discount.title
                            });
                            setShowCouponModal(true);
                          }}
                          className="flex items-center space-x-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                        >
                          <Icons.Ticket className="h-4 w-4" />
                          <span>Kuponkód megtekintése</span>
                        </button>
                      )}
                    </div>

                    <div className="mt-3 text-sm text-gray-600 discount-description" 
                         dangerouslySetInnerHTML={{ __html: discount.description }} />
                    
                    <div className="mt-4 flex items-center text-sm text-indigo-600">
                      <span>Részletek megtekintése</span>
                      <Icons.ArrowRight className="ml-2 h-4 w-4" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Kuponkód Modal */}
        {showCouponModal && selectedCoupon && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-sm w-full p-6 relative">
              <button
                onClick={() => setShowCouponModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
              >
                <Icons.X className="h-5 w-5" />
              </button>
              
              <div className="text-center">
                <Icons.Ticket className="mx-auto h-12 w-12 text-indigo-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {selectedCoupon.title}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Használd ezt a kuponkódot a kedvezmény érvényesítéséhez
                </p>
                
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-2xl font-mono font-bold text-indigo-600 select-all">
                    {selectedCoupon.code}
                  </p>
                </div>
                
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(selectedCoupon.code);
                    toast.success('Kuponkód másolva!');
                  }}
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <Icons.Copy className="h-4 w-4 mr-2" />
                  Kuponkód másolása
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="mt-16 pt-10 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Előny Klub */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Előny Klub</h3>
              <p className="text-sm text-gray-600">
                Az Előny Klub egy olyan közösség, ahol mindenki megtalálja a számára legmegfelelőbb kedvezményeket és előnyöket.
              </p>
            </div>

            {/* Kategóriák */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Kategóriák</h3>
              <ul className="space-y-2">
                {categories.slice(0, 5).map(category => (
                  <li key={category.id}>
                    <button 
                      onClick={() => setSelectedCategory(category.id)}
                      className="text-sm text-gray-600 hover:text-indigo-600"
                    >
                      {category.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Alsó footer */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-sm text-gray-500">
                &copy; {new Date().getFullYear()} Előny Klub. Minden jog fenntartva.
              </p>
              <div className="mt-4 md:mt-0 flex space-x-6">
                <a href="#" className="text-sm text-gray-500 hover:text-indigo-600">
                  Adatvédelmi irányelvek
                </a>
                <a href="#" className="text-sm text-gray-500 hover:text-indigo-600">
                  Felhasználási feltételek
                </a>
                <a href="#" className="text-sm text-gray-500 hover:text-indigo-600">
                  Cookie-k
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
} 